/**
 * 雙層時間軸表頭：上層半年／月／季／年，下層日／週／月／季。
 * 只渲染可視範圍的刻度（橫向虛擬化）。
 *
 * 視覺對齊 MS Project：白底、細灰欄位分隔線、上下層之間一條細橫線，
 * 上層標籤在期間被捲出畫面時會貼齊左緣，維持「現在在哪一段」的可讀性。
 */

import type { ReactElement } from 'react';

import type { TimeAxis, TimelineTicks } from './time-scale.js';
import { timeToX } from './time-scale.js';

export const HEADER_TIER_HEIGHT = 22;
export const HEADER_HEIGHT = HEADER_TIER_HEIGHT * 2;

/** 上層標籤貼齊左緣所需的最小欄寬。 */
const STICKY_MIN_WIDTH = 90;

export interface TimelineHeaderProps {
  readonly axis: TimeAxis;
  readonly ticks: TimelineTicks;
  readonly todayMs: number;
  readonly scrollLeft: number;
  readonly width: number;
}

export function TimelineHeader({
  axis,
  ticks,
  todayMs,
  scrollLeft,
  width,
}: TimelineHeaderProps): ReactElement {
  const todayX = timeToX(axis, todayMs);
  const showToday = todayMs >= axis.originMs && todayMs <= axis.endMs;

  return (
    <div
      className="relative overflow-hidden border-b border-line-strong bg-raised"
      style={{ height: HEADER_HEIGHT, width }}
      data-testid="gantt-header"
    >
      <div style={{ transform: `translateX(${-scrollLeft}px)`, willChange: 'transform' }}>
        <svg
          width={axis.totalWidth}
          height={HEADER_HEIGHT}
          className="block"
          role="presentation"
        >
          {ticks.upper.map((tick) => {
            const sticky =
              tick.width > STICKY_MIN_WIDTH
                ? Math.min(Math.max(tick.x, scrollLeft), tick.x + tick.width - STICKY_MIN_WIDTH)
                : tick.x;
            return (
              <g key={`u-${tick.startMs}`} data-testid="gantt-tick-upper">
                <line
                  x1={tick.x}
                  y1={0}
                  x2={tick.x}
                  y2={HEADER_HEIGHT}
                  className="stroke-line"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={sticky + 8}
                  y={HEADER_TIER_HEIGHT - 7}
                  className="fill-fg text-[11px] font-medium"
                >
                  {tick.width > 34 ? tick.label : ''}
                </text>
              </g>
            );
          })}
          <line
            x1={0}
            y1={HEADER_TIER_HEIGHT}
            x2={axis.totalWidth}
            y2={HEADER_TIER_HEIGHT}
            className="stroke-line"
            strokeWidth={1}
            shapeRendering="crispEdges"
          />
          {ticks.lower.map((tick) => (
            <g key={`l-${tick.startMs}`} data-testid="gantt-tick-lower">
              {tick.isNonWorking ? (
                <rect
                  x={tick.x}
                  y={HEADER_TIER_HEIGHT + 1}
                  width={tick.width}
                  height={HEADER_TIER_HEIGHT - 1}
                  className="fill-sunken"
                />
              ) : null}
              <line
                x1={tick.x}
                y1={HEADER_TIER_HEIGHT + 4}
                x2={tick.x}
                y2={HEADER_HEIGHT}
                className="stroke-grid-major"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              <text
                x={tick.x + tick.width / 2}
                y={HEADER_HEIGHT - 7}
                textAnchor="middle"
                className="fill-fg-muted text-[10px]"
              >
                {tick.width > 14 ? tick.label : ''}
              </text>
            </g>
          ))}
          {showToday ? (
            <g data-testid="gantt-today-header">
              <line
                x1={todayX}
                y1={HEADER_TIER_HEIGHT}
                x2={todayX}
                y2={HEADER_HEIGHT}
                className="stroke-accent"
                strokeWidth={1}
              />
              <polygon
                points={`${todayX - 3.5},${HEADER_HEIGHT - 5} ${todayX + 3.5},${HEADER_HEIGHT - 5} ${todayX},${HEADER_HEIGHT}`}
                className="fill-accent"
              />
            </g>
          ) : null}
        </svg>
      </div>
    </div>
  );
}
